import { Box, Checkbox, Pagination, Paper, Table, TableBody, TableContainer, TableHead } from '@mui/material';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import FiltersMenu from 'components/filters-menu';
import { StyledFilterIcon } from 'components/filters-menu/styles';
import UPSearchInput from 'components/input-with-search';
import UPWeekPicker from 'components/week-picker';
import { toNumber } from 'lodash';
import { getCurrentTime, getISO8601WeekNumber, getISO8601Year } from 'modules/contracts/helpers';
import { PageFilter } from 'modules/contracts/types';
import DashboardConfirmedKpiTile from 'modules/dashboard/tiles/confirmed-kpi/DashboardConfirmedKpiTile';
import DashboardNoShowKpiTile from 'modules/dashboard/tiles/no-show-kpi/DashboardNoShowKpiTile';
import DashboardPlanningKpisTile from 'modules/dashboard/tiles/planning-kpis/DashboardPlanningKpisTile';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { archiveErrorsAction, getErrorTypesAction, getEventsAction } from 'store/actions/api-errors-actions';
import { apiErrorsSelector, getApiErrorsLoadingSelector } from 'store/selectors/api-errors-selectors';
import {
  DASHBOARD__BUSINESS_OBJECT,
  DASHBOARD__EVENT_LOGS,
  DASHBOARD__EVENT_SOURCE,
  DASHBOARD__EVENT_TYPE,
  DASHBOARD__KPIS,
  DASHBOARD__SHOW_ARCHIVED,
  GENERAL__NO,
  GENERAL__NO_DATA_TO_SHOW,
  GENERAL__SEARCH,
  GENERAL__YES,
  HEADER__WEEK,
} from 'translations/constants';
import {
  getEventBusinessObjectsAsDropdownItems,
  getEventSourcesAsDropdownItems,
  getEventTypesAsDropdownItems,
} from 'types/errors/ApiErrorBusinessObjects';
import { ApiErrorFilters } from 'types/errors/ApiErrorFilters';
import APiErrorBottomBar from './ApiErrorBottomBar';
import ApiErrorRow from './ApiErrorRow';
import {
  AdminDashboardTitle,
  DashboardFilterButton,
  HeaderContainer,
  SearchInputContainer,
  SearchWithFilterContainer,
  StyledTableCell,
  StyledTableRow,
  TilesRow,
  TitleWithWeekContainer,
} from './styles';

const AdminDashboard = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const apiErrors = useAppSelector(apiErrorsSelector);
  const isLoading = useAppSelector(getApiErrorsLoadingSelector);

  const week = useRef(getCurrentTime().date);
  const [currentPage, setCurrentPage] = useState(1);
  const [type, setType] = useState('');
  const [source, setSource] = useState('');
  const [includeArchived, setIncludeArchived] = useState<string>('2');
  const [businessObject, setBusinessObject] = useState<string>('');
  const [errorDate, setErrorDate] = useState<Date>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [resetOpenKey, setResetOpenKey] = useState(0);

  const pageSize = 25;

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [clearAll, setClearAll] = useState<boolean>(true);

  const [filtersPopoverAnchorEl, setFiltersPopoverAnchorEl] = useState<null | HTMLElement>(null);

  const filters = useMemo((): PageFilter[] => {
    const includeArchivedFilterOptions: UPDropdownItem[] = [
      {
        value: '1',
        label: t(GENERAL__YES),
      },
      {
        value: '2',
        label: t(GENERAL__NO),
      },
    ];

    const f: PageFilter[] = [
      {
        label: t(DASHBOARD__BUSINESS_OBJECT),
        value: businessObject,
        setValue: setBusinessObject,
        items: getEventBusinessObjectsAsDropdownItems(),
        component: UPDropdown,
        canBeEmpty: true,
      },
      {
        label: t(DASHBOARD__EVENT_TYPE),
        value: type,
        setValue: setType,
        items: getEventTypesAsDropdownItems(),
        component: UPDropdown,
        canBeEmpty: true,
      },
      {
        label: t(DASHBOARD__EVENT_SOURCE),
        value: source,
        setValue: setSource,
        items: getEventSourcesAsDropdownItems(),
        component: UPDropdown,
        canBeEmpty: true,
      },
      {
        label: t(DASHBOARD__SHOW_ARCHIVED),
        value: includeArchived,
        setValue: setIncludeArchived,
        items: includeArchivedFilterOptions,
        component: UPDropdown,
        canBeEmpty: false,
      },
    ];

    return f;
  }, [t, businessObject, type, source, includeArchived]);

  const getData = useCallback(
    (pageNumber: number) => {
      const filter: ApiErrorFilters = {
        pageNumber,
        pageSize,
        includeArchived: includeArchived === '1',
        date: errorDate,
        week: getISO8601WeekNumber(week.current),
        businessObject: businessObject ? toNumber(businessObject) : null,
        year: getISO8601Year(week.current),
        source: source ? toNumber(source) : -1,
        type: type ? toNumber(type) : -1,
        searchInput,
      };
      dispatch(getEventsAction(filter));
    },
    [dispatch, includeArchived, errorDate, businessObject, source, type, searchInput],
  );

  useEffect(() => {
    if (clearAll) {
      setSelectedRows([]);
      setClearAll(false);
    }
  }, [clearAll]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getData(page);
    setResetOpenKey(prev => prev + 1);
  };

  const getDefaultData = () => {
    const defaultFilters: ApiErrorFilters = {
      pageNumber: 1,
      pageSize,
      includeArchived: false,
      week: getISO8601WeekNumber(week.current),
      year: getISO8601Year(week.current),
      type: -1,
      source: -1,
    };
    dispatch(getEventsAction(defaultFilters));
  };

  const resetFilters = () => {
    setCurrentPage(1);
    setBusinessObject('');
    setType('-1');
    setSource('-1');
    setIncludeArchived('2');
    setErrorDate(null);
    getDefaultData();
  };

  useEffect(() => {
    getDefaultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getErrorTypesAction());
  }, [dispatch]);

  const onClickFilterButton = event => {
    setFiltersPopoverAnchorEl(event.currentTarget);
  };

  const onCloseFilterPopover = () => {
    setFiltersPopoverAnchorEl(null);
  };

  const onApplyFilters = () => {
    setCurrentPage(1);
    getData(1);
    onCloseFilterPopover();
  };

  const onArchiveErrors = () => {
    dispatch(archiveErrorsAction(selectedRows)).then(() => {
      setSelectedRows([]);
      getData(1);
    });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = apiErrors.items.map(n => n.id);
      setSelectedRows(newSelected);
      return;
    }
    setSelectedRows([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1));
    }
    setSelectedRows(newSelected);
  };

  const isItemChecked = (id: number) => selectedRows.indexOf(id) !== -1;

  const getRows = () => {
    if (apiErrors?.items) {
      const rows = apiErrors.items.map(r => (
        <ApiErrorRow
          key={r.id}
          row={r}
          handleClick={handleClick}
          isChecked={isItemChecked(r.id)}
          resetOpenKey={resetOpenKey}
        />
      ));
      return rows;
    }

    return <p>No data to show</p>;
  };

  return (
    <Box>
      <Box mb={1}>
        <AdminDashboardTitle>{t(DASHBOARD__KPIS)}</AdminDashboardTitle>
      </Box>
      <TilesRow>
        <DashboardPlanningKpisTile />
        <DashboardNoShowKpiTile />
        <DashboardConfirmedKpiTile />
      </TilesRow>
      <HeaderContainer>
        <TitleWithWeekContainer>
          <AdminDashboardTitle>{t(DASHBOARD__EVENT_LOGS)}</AdminDashboardTitle>
          <UPWeekPicker
            value={week.current}
            weekValue={getISO8601WeekNumber(week.current)}
            label={t(HEADER__WEEK)}
            onChange={newValue => {
              week.current = newValue;
              handlePageChange(null, 1);
              setResetOpenKey(prev => prev + 1);
            }}
            className="datepicker--white-background"
          />
        </TitleWithWeekContainer>
        <SearchWithFilterContainer>
          <SearchInputContainer>
            <UPSearchInput
              label={t(GENERAL__SEARCH)}
              placeholder={t(GENERAL__SEARCH)}
              value={searchInput}
              disabled={isLoading}
              onChange={value => {
                setSearchInput(value);
                if (!value) setSearchInput('');
              }}
              onSearch={() => {
                setSearchInput(searchInput);
                onApplyFilters();
              }}
            />
          </SearchInputContainer>
          <Box display="flex" alignItems="flex-end">
            <DashboardFilterButton
              text={
                <Box display="flex" alignItems="center">
                  <StyledFilterIcon />
                </Box>
              }
              onClick={onClickFilterButton}
              ref={filtersPopoverAnchorEl}
            />
          </Box>
        </SearchWithFilterContainer>
        <FiltersMenu
          open={Boolean(filtersPopoverAnchorEl)}
          anchorEl={filtersPopoverAnchorEl}
          onClose={onCloseFilterPopover}
          onApplyFilters={onApplyFilters}
          onClearFilters={resetFilters}
          filters={filters}
        />
      </HeaderContainer>
      {apiErrors?.items?.length > 0 && (
        <>
          <Box my={2.5} mx={0.75} id="hasErrors">
            <TableContainer component={Paper} id="tableContainer" style={{ backgroundColor: '#fff' }}>
              <Table aria-label="collapsible table" id="table" style={{ backgroundColor: '#fff' }}>
                <TableHead>
                  <StyledTableRow>
                    <StyledTableCell padding="checkbox" align="left">
                      {includeArchived === '2' && (
                        <Checkbox
                          color="primary"
                          onChange={handleSelectAllClick}
                          checked={selectedRows.length > 0 && selectedRows.length === apiErrors?.items?.length}
                          indeterminate={selectedRows.length > 0 && selectedRows.length < apiErrors?.items?.length}
                          inputProps={{
                            'aria-labelledby': `errors-select-all`,
                          }}
                        />
                      )}
                    </StyledTableCell>
                    <StyledTableCell width="3%" />
                    <StyledTableCell align="center">Date</StyledTableCell>
                    <StyledTableCell width="10%">Business Object</StyledTableCell>
                    <StyledTableCell width="5%">Type</StyledTableCell>
                    <StyledTableCell width="8%">Source</StyledTableCell>
                    <StyledTableCell width="15%">Company</StyledTableCell>
                    <StyledTableCell>Title</StyledTableCell>
                    <StyledTableCell align="center" />
                  </StyledTableRow>
                </TableHead>
                <TableBody>{getRows()}</TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Pagination count={apiErrors?.totalPages} page={currentPage} onChange={handlePageChange} />
        </>
      )}
      {!apiErrors?.items?.length && (
        <TableContainer component={Paper} id="no_data_tableContainer">
          <Table aria-label="collapsible table" id="table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell colSpan={5} align="center">
                  {t(GENERAL__NO_DATA_TO_SHOW)}
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
          </Table>
        </TableContainer>
      )}
      {selectedRows.length > 0 && (
        <APiErrorBottomBar
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setClearAll={setClearAll}
          onArchiveErrors={onArchiveErrors}
        />
      )}
    </Box>
  );
};

export default AdminDashboard;
